/**
 * Smart (Ella) playlist queue top-up (Paul, Oct 23 2026 — "Ariana Grande · Kindred Spirits").
 *
 * An Ella playlist starts playing as soon as its first batch exists (8 tracks); the server keeps
 * building to the full set (35). The build poll only refreshed the page's track list — the playback
 * queue stayed at the first batch, so music stopped after track 8. When the playing queue is this
 * playlist and is nearly used up, fetch the server list and append the tracks the queue is missing.
 * Ella Radio sessions are excluded (they have their own refill / build-poll path).
 */
import { useEffect } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { formatSmartPlaylistTrackForPlayback, normalizeSmartPlaylistDetailTrack } from './smartPlaylistDetailTracks';

/** Top up when this many (or fewer) tracks remain after the current one. */
export const TOP_UP_REMAINING_THRESHOLD = 3;
const TOP_UP_DEBOUNCE_MS = 15_000;

let _inFlight = false;
let _lastAttemptAt = 0;

function serverTrackId(track: unknown): string {
  const n = normalizeSmartPlaylistDetailTrack(track) as { id?: unknown } | undefined;
  return n?.id != null ? String(n.id) : '';
}

/**
 * Tracks to append so the queue reaches the end of the server playlist.
 * Returns [] unless every queued track belongs to this playlist (the queue is this playlist).
 */
export function computeSmartPlaylistTopUp(queueTrackIds: string[], serverTracks: unknown[]): unknown[] {
  if (!queueTrackIds.length || !serverTracks.length) return [];
  const serverIds = serverTracks.map(serverTrackId);
  const serverSet = new Set(serverIds.filter(Boolean));
  if (!queueTrackIds.every((id) => serverSet.has(id))) return [];
  const queued = new Set(queueTrackIds);
  let lastQueuedPos = -1;
  serverIds.forEach((id, i) => {
    if (queued.has(id)) lastQueuedPos = i;
  });
  return serverTracks
    .slice(lastQueuedPos + 1)
    .filter((t) => {
      const id = serverTrackId(t);
      return id !== '' && !queued.has(id);
    })
    .map(formatSmartPlaylistTrackForPlayback);
}

function activeSmartPlaylistId(): string | null {
  try {
    if (sessionStorage.getItem('active_ella_radio') === 'true') return null;
    return sessionStorage.getItem('active_smart_playlist') || null;
  } catch {
    return null;
  }
}

/** One top-up attempt for the current store state. Exported for tests. */
export async function maybeTopUpSmartPlaylistQueue(nowMs = Date.now()): Promise<number> {
  const playlistId = activeSmartPlaylistId();
  if (!playlistId || _inFlight || nowMs - _lastAttemptAt < TOP_UP_DEBOUNCE_MS) return 0;
  const { queue, queueIndex } = useMaestroStore.getState();
  if (queueIndex < 0 || !queue.length) return 0;
  if (queue.length - queueIndex - 1 > TOP_UP_REMAINING_THRESHOLD) return 0;
  try {
    if (getSyncHandler().isObserving()) return 0;
  } catch { /* no sync handler — controller */ }

  _inFlight = true;
  _lastAttemptAt = nowMs;
  try {
    const { smartPlaylistService } = await import('../services/smartPlaylistService');
    const res = await smartPlaylistService.getDetail(playlistId);
    if (!res?.success) return 0;
    const live = useMaestroStore.getState().queue;
    const queueIds = live.map((item: any) => String(item?.track?.id ?? item?.track?.qobuzTrackId ?? ''));
    const toAppend = computeSmartPlaylistTopUp(queueIds, res.tracks || []);
    if (!toAppend.length) return 0;
    const { QueueController } = await import('../transport/QueueController');
    QueueController.appendToQueue(toAppend, 'smartPlaylistQueueTopUp');
    diagnosticLog('info', 'playback', 'smart_playlist_queue_topup', {
      playlistId,
      appended: toAppend.length,
      queueLengthBefore: live.length,
      serverCount: (res.tracks || []).length,
    });
    return toAppend.length;
  } catch (e) {
    diagnosticLog('warn', 'playback', 'smart_playlist_queue_topup_failed', {
      playlistId,
      error: String((e as Error)?.message || e),
    });
    return 0;
  } finally {
    _inFlight = false;
  }
}

/** Test hook. */
export function __resetSmartPlaylistTopUpForTests(): void {
  _inFlight = false;
  _lastAttemptAt = 0;
}

/** Global hook (mounted in AppLayout): keeps a playing smart-playlist queue topped up to the full list. */
export function useSmartPlaylistQueueTopUp(): void {
  const queueIndex = useMaestroStore((s) => s.queueIndex);
  const queueLength = useMaestroStore((s) => s.queue.length);
  const playbackState = useMaestroStore((s) => s.playbackState);

  useEffect(() => {
    if (playbackState !== 'playing' && playbackState !== 'paused' && playbackState !== 'loading') return;
    void maybeTopUpSmartPlaylistQueue();
  }, [queueIndex, queueLength, playbackState]);
}
